import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ContractAccount,
  ContractAccountStatus,
  SellContract,
  ContractCard,
  CreateContractAccountRequest,
  UpdateContractAccountRequest,
  CreateSellContractRequest,
  GetSellContractsRequest,
  GetSellContractsResult,
  GetContractAccountListResult,
  PublicSellContractResponse,
  ContractPublicOrderStatus,
  CorrectPublicSellContractCredentialsRequest,
  getContractAccountsRequest,
  UpdateSellContractRequest,
  ArchiveSellContractsRequest,
  QuickSellLeftoversRequest,
  UpdateContractBillingRequest,
  GetAdminSellContractsRequest,
  GetAdminSellContractsResult,
  GetSellContractStatsRequest,
  SellContractStatsReport,
  OutsourcingHandoffResponse,
  UpdateOutsourcingHandoffCredentialsRequest,
  ProviderOrderStatusResponse,
  ProviderOrderActionResponse,
  ProviderCredentialCorrectionRequest,
  SellContractDailyReportResponse,
} from './player-auction.type';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class PlayerAuctionService {
  private accountUrl = environment.apiBaseURL + '/player-auction/account';
  private contractUrl = environment.apiBaseURL + '/player-auction/sell-contract';
  private publicContractUrl = environment.apiBaseURL + '/player-auction/public/sell-contract';
  private providerOrderUrl = environment.apiBaseURL + '/outsourcing';
  private adminContractUrl = environment.apiBaseURL + '/player-auction/admin/sell-contract';

  constructor(private http: HttpClient) { }

  // Contract Accounts
  getContractAccounts(request: getContractAccountsRequest): Observable<GetContractAccountListResult> {
    let params = new HttpParams();
    if (request.status) params = params.set('status', request.status);
    if (request.search !== undefined) params = params.set('search', request.search);
    if (request.pageIndex !== undefined) params = params.set('pageIndex', request.pageIndex.toString());
    if (request.pageSize !== undefined) params = params.set('pageSize', request.pageSize.toString());
    return this.http
      .get<{ result: GetContractAccountListResult }>(this.accountUrl, { params })
      .pipe(map(r => r.result));
  }

  createContractAccount(request: CreateContractAccountRequest): Observable<ContractAccount> {
    return this.http
      .post<{ result: ContractAccount }>(this.accountUrl, request)
      .pipe(map(r => r.result));
  }

  updateContractAccount(id: string, request: UpdateContractAccountRequest): Observable<string> {
    return this.http
      .patch<{ result: string }>(`${this.accountUrl}/${id}/status`, request)
      .pipe(map(r => r.result));
  }

  deleteContractAccount(id: string): Observable<string> {
    return this.http
      .delete<{ result: string }>(`${this.accountUrl}/${id}`)
      .pipe(map(r => r.result));
  }

  // Sell Contracts
  getSellContracts(request: GetSellContractsRequest = {}): Observable<GetSellContractsResult> {
    let params = new HttpParams();
    if (request.pageIndex !== undefined) params = params.set('pageIndex', request.pageIndex.toString());
    if (request.pageSize !== undefined) params = params.set('pageSize', request.pageSize.toString());
    if (request.status) params = params.set('status', request.status);
    if (request.search) params = params.set('search', request.search);
    if (request.archived !== undefined) params = params.set('archived', request.archived.toString());
    if (request.in_pool !== undefined) params = params.set('in_pool', request.in_pool.toString());
    if (request.quick_selling !== undefined) params = params.set('quick_selling', request.quick_selling.toString());
    if (request.date_from) params = params.set('date_from', request.date_from);
    if (request.date_to) params = params.set('date_to', request.date_to);
    return this.http
      .get<{ result: GetSellContractsResult }>(this.contractUrl, { params })
      .pipe(map(r => r.result));
  }

  /** GET /player-auction/sell-contract/stats-report — aggregate stats for the caller's visible contracts matching the given filters */
  getSellContractStatsReport(request: GetSellContractStatsRequest = {}): Observable<SellContractStatsReport> {
    let params = new HttpParams();
    if (request.search) params = params.set('search', request.search);
    if (request.archived !== undefined) params = params.set('archived', request.archived.toString());
    if (request.in_pool !== undefined) params = params.set('in_pool', request.in_pool.toString());
    if (request.date_from) params = params.set('date_from', request.date_from);
    if (request.date_to) params = params.set('date_to', request.date_to);
    return this.http
      .get<{ result: SellContractStatsReport }>(`${this.contractUrl}/stats-report`, { params })
      .pipe(map(r => r.result));
  }

  /** GET /player-auction/sell-contract/open-counts — caller-visible sell contracts per open status, for the page tabs. Reads no cards, so it stays fast. */
  getSellContractOpenCounts(): Observable<Partial<Record<string, number>>> {
    return this.http
      .get<{ result: { status_counts: Partial<Record<string, number>> } }>(`${this.contractUrl}/open-counts`)
      .pipe(map(r => r.result.status_counts));
  }

  /** Finished sell contracts per Tehran day (the day they finished), split into paid and unpaid. */
  getSellContractsDailyReport(request: { fromDate?: string; toDate?: string; pageIndex?: number; pageSize?: number }): Observable<SellContractDailyReportResponse> {
    let params = new HttpParams();
    for (const [k, v] of Object.entries(request)) {
      if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
    }
    return this.http
      .get<{ result: SellContractDailyReportResponse }>(`${this.contractUrl}/daily-report`, { params })
      .pipe(map(r => r.result));
  }

  archiveSellContract(request: ArchiveSellContractsRequest): Observable<SellContract> {
    return this.http
      .patch<{ result: SellContract }>(`${this.contractUrl}/archive`, request)
      .pipe(map(r => r.result));
  }

  getSellContract(id: string): Observable<SellContract> {
    return this.http
      .get<{ result: SellContract }>(`${this.contractUrl}/${id}`)
      .pipe(map(r => r.result));
  }

  createSellContract(request: CreateSellContractRequest): Observable<SellContract> {
    return this.http
      .post<{ result: SellContract }>(this.contractUrl, request)
      .pipe(map(r => r.result));
  }

  getContractCards(id: string): Observable<ContractCard[]> {
    return this.http
      .get<{ result: ContractCard[] }>(`${this.contractUrl}/${id}/cards`)
      .pipe(map(r => r.result));
  }

  resumeSellContract(id: string): Observable<SellContract> {
    return this.http
      .patch<{ result: SellContract }>(`${this.contractUrl}/${id}/resume`, {})
      .pipe(map(r => r.result));
  }

  cancelSellContract(id: string): Observable<SellContract> {
    return this.http
      .patch<{ result: SellContract }>(`${this.contractUrl}/${id}/cancel`, {})
      .pipe(map(r => r.result));
  }

  /** Pauses listing on a Transferring contract; resume continues it in place. */
  stopSellContract(id: string): Observable<SellContract> {
    return this.http
      .patch<{ result: SellContract }>(`${this.contractUrl}/${id}/stop`, {})
      .pipe(map(r => r.result));
  }

  patchSellContract(id: string, req: UpdateSellContractRequest): Observable<SellContract> {
    return this.http
      .patch<{ result: SellContract }>(`${this.contractUrl}/${id}`, req)
      .pipe(map(r => r.result));
  }

  /** POST /player-auction/sell-contract/quick-sell-leftovers — force quick sell leftover cards on Canceled/Failed contracts */
  quickSellLeftovers(request: QuickSellLeftoversRequest): Observable<string> {
    return this.http
      .post<{ result: string }>(`${this.contractUrl}/quick-sell-leftovers`, request)
      .pipe(map(r => r.result));
  }

  getContractLog(id: string): Observable<string> {
    return this.http.get(`${this.contractUrl}/${id}/log`, { responseType: 'text' });
  }

  getPublicContractCards(id: string): Observable<ContractCard[]> {
    return this.http
      .get<{ result: ContractCard[] }>(`${this.publicContractUrl}/${id}/cards`)
      .pipe(map(r => r.result));
  }

  getPublicSellContract(id: string): Observable<PublicSellContractResponse> {
    return this.http
      .get<{ result: PublicSellContractResponse }>(`${this.publicContractUrl}/${id}`)
      .pipe(map(r => r.result));
  }

  /** GET /player-auction/public/sell-contract/:id/report — no auth required */
  getPublicContractReport(id: string): Observable<ContractPublicOrderStatus> {
    return this.http
      .get<{ result: ContractPublicOrderStatus }>(`${this.publicContractUrl}/${id}/report`)
      .pipe(map(r => r.result));
  }

  /**
   * PATCH /player-auction/public/sell-contract/:id/correct-credentials — no auth required.
   * Only succeeds when the contract is Failed with a wrong_password/wrong_backup_code reason.
   */
  correctPublicSellContractCredentials(
    id: string,
    request: CorrectPublicSellContractCredentialsRequest,
  ): Observable<PublicSellContractResponse> {
    return this.http
      .patch<{ result: PublicSellContractResponse }>(`${this.publicContractUrl}/${id}/correct-credentials`, request)
      .pipe(map(r => r.result));
  }

  /**
   * PATCH /player-auction/public/sell-contract/:id/resume — no auth required.
   * Only succeeds for Failed reasons that don't require new credentials
   * (signed_into_another_device, transfer_list_full, not_enough_coin, two_factor_not_enabled).
   */
  resumePublicSellContract(id: string): Observable<PublicSellContractResponse> {
    return this.http
      .patch<{ result: PublicSellContractResponse }>(`${this.publicContractUrl}/${id}/resume`, {})
      .pipe(map(r => r.result));
  }

  /**
   * GET /player-auction/public/sell-contract/:id/screenshot — no auth required.
   * Source defaults to provider for outsourced contracts and local otherwise.
   */
  getPublicSellContractScreenshot(id: string, type: 'start' | 'end' = 'start'): Observable<Blob> {
    return this.http.get(`${this.publicContractUrl}/${id}/screenshot?type=${type}`, {
      responseType: 'blob',
    });
  }

  // Admin billing management
  adminGetSellContracts(request: GetAdminSellContractsRequest = {}): Observable<GetAdminSellContractsResult> {
    let params = new HttpParams();
    if (request.pageIndex !== undefined) params = params.set('pageIndex', request.pageIndex.toString());
    if (request.pageSize !== undefined) params = params.set('pageSize', request.pageSize.toString());
    if (request.status) params = params.set('status', request.status);
    if (request.search) params = params.set('search', request.search);
    if (request.ownerRole) params = params.set('ownerRole', request.ownerRole);
    if (request.archived !== undefined) params = params.set('archived', request.archived.toString());
    if (request.quick_selling !== undefined) params = params.set('quick_selling', request.quick_selling.toString());
    if (request.date_from) params = params.set('date_from', request.date_from);
    if (request.date_to) params = params.set('date_to', request.date_to);
    return this.http
      .get<{ result: GetAdminSellContractsResult }>(this.adminContractUrl, { params })
      .pipe(map(r => r.result));
  }

  updateSellContractBilling(id: string, req: UpdateContractBillingRequest): Observable<SellContract> {
    return this.http
      .patch<{ result: SellContract }>(`${this.adminContractUrl}/${id}/billing`, req)
      .pipe(map(r => r.result));
  }

  updateSellContractPriority(id: string, priority: boolean): Observable<SellContract> {
    return this.http
      .patch<{ result: SellContract }>(`${this.adminContractUrl}/${id}/priority`, { priority })
      .pipe(map(r => r.result));
  }

  // Outsourcing handoff (admin)
  /** POST .../outsourcing-handoff — starts a handoff for the contract's remaining coin target. 202 on success. */
  startOutsourcingHandoff(id: string): Observable<OutsourcingHandoffResponse> {
    return this.http
      .post<{ result: OutsourcingHandoffResponse }>(`${this.contractUrl}/${id}/outsourcing-handoff`, {})
      .pipe(map(r => r.result));
  }

  /** GET .../outsourcing-handoff — 404 if no handoff exists for this contract. */
  getOutsourcingHandoff(id: string): Observable<OutsourcingHandoffResponse> {
    return this.http
      .get<{ result: OutsourcingHandoffResponse }>(`${this.contractUrl}/${id}/outsourcing-handoff`)
      .pipe(map(r => r.result));
  }

  /** GET .../outsourcing-handoff/screenshot?type=start|end — raw image/jpeg, not JSON. */
  getOutsourcingHandoffScreenshot(id: string, type: 'start' | 'end' = 'start'): Observable<Blob> {
    return this.http.get(`${this.contractUrl}/${id}/outsourcing-handoff/screenshot?type=${type}`, {
      responseType: 'blob',
    });
  }

  /** PATCH .../outsourcing-handoff/retry — re-attempts a handoff sitting in NeedsReview. */
  retryOutsourcingHandoff(id: string): Observable<OutsourcingHandoffResponse> {
    return this.http
      .patch<{ result: OutsourcingHandoffResponse }>(`${this.contractUrl}/${id}/outsourcing-handoff/retry`, {})
      .pipe(map(r => r.result));
  }

  /** PATCH .../outsourcing-handoff/credentials — at least one of password/backup_codes is required. */
  correctOutsourcingHandoffCredentials(id: string, request: UpdateOutsourcingHandoffCredentialsRequest): Observable<OutsourcingHandoffResponse> {
    return this.http
      .patch<{ result: OutsourcingHandoffResponse }>(`${this.contractUrl}/${id}/outsourcing-handoff/credentials`, request)
      .pipe(map(r => r.result));
  }

  // Public provider-order tracking (no auth). `provider` defaults server-side to the configured provider.
  private providerParams(provider?: string | null): HttpParams {
    return provider ? new HttpParams().set('provider', provider) : new HttpParams();
  }

  /** GET /outsourcing/:orderID/status — NOT wrapped in { result }. */
  getProviderOrderStatus(orderId: string, provider?: string | null): Observable<ProviderOrderStatusResponse> {
    return this.http.get<ProviderOrderStatusResponse>(`${this.providerOrderUrl}/${orderId}/status`, {
      params: this.providerParams(provider),
    });
  }

  /** GET /outsourcing/:orderID/screenshot?type=start|end — raw image/jpeg. */
  getProviderOrderScreenshot(orderId: string, type: 'start' | 'end', provider?: string | null): Observable<Blob> {
    return this.http.get(`${this.providerOrderUrl}/${orderId}/screenshot`, {
      params: this.providerParams(provider).set('type', type),
      responseType: 'blob',
    });
  }

  /** POST /outsourcing/:orderID/resume — mode is resume or stop. */
  updateProviderOrderState(orderId: string, mode: 'resume' | 'stop', provider?: string | null): Observable<ProviderOrderActionResponse> {
    return this.http
      .post<{ result: ProviderOrderActionResponse }>(`${this.providerOrderUrl}/${orderId}/resume`, { mode }, {
        params: this.providerParams(provider),
      })
      .pipe(map(r => r.result));
  }

  /** POST /outsourcing/:orderID/correct-credentials */
  correctProviderOrderCredentials(orderId: string, request: ProviderCredentialCorrectionRequest, provider?: string | null): Observable<unknown> {
    return this.http
      .post<{ result: unknown }>(`${this.providerOrderUrl}/${orderId}/correct-credentials`, request, {
        params: this.providerParams(provider),
      })
      .pipe(map(r => r.result));
  }
}
